import * as React from "react";
import { ChevronDown, ChevronUp } from "react-feather";
import { ExternalLink } from "../../../../newcomponents/table/components/ExternalLink";
import { SyntaxHighlight } from "../../../../components/syntaxHighlight/SyntaxHighlight";
import { CopyButtonWithApproval } from "../../../domain/create/copyButtonWithApproval";
import { useCpFormValue } from "../../../gvc/testNewForm/utils";
import { CpInput } from "../../../gvc/testNewForm/CpInput";
import { CpFormItem } from "../../../gvc/testNewForm/CpFormItem";
import { CpLabel } from "../../../gvc/testNewForm/CpLabel";
import { CpErrorWarning } from "../../../gvc/testNewForm/CpErrorWarning";

interface Props {}
export const AWSRoleARNGuide: React.FC<Props> = ({}) => {
  const name = useCpFormValue("name");
  const awsAccountId = useCpFormValue("aws.accountId");
  const instructions = useCpFormValue("aws.instructions");

  const [continueWith, setContinueWith] = React.useState<"ui" | "cli" | "none">("none");
  const [showAwsAccountIdGuide, setShowAwsAccountIdGuide] = React.useState(false);

  function onConsole() {
    if (continueWith === "ui") {
      setContinueWith("none");
    } else {
      setContinueWith("ui");
    }
  }

  function onCPLN() {
    if (continueWith === "cli") {
      setContinueWith("none");
    } else {
      setContinueWith("cli");
    }
  }

  if (!instructions.data) {
    return <div>Instructions are not found, close this drawer and try again.</div>;
  }

  return (
    <>
      <div className="my-4">
        <button type={"button"} className="ngfocus color-link font-medium" onClick={onConsole}>
          Continue with AWS UI
          {continueWith === "ui" ? (
            <ChevronUp className="ml-1 inline-block" size={16} style={{ transform: "translateY(2px)" }} />
          ) : (
            <ChevronDown className="ml-1 inline-block" size={16} style={{ transform: "translateY(2px)" }} />
          )}
        </button>
      </div>
      {continueWith === "ui" ? (
        <>
          <ul className="pl-8">
            <li className="list-decimal mb-6 ml-4">
              Sign into the <ExternalLink url="https://console.aws.amazon.com" label="AWS Console" disableCopy />
            </li>
            <li className="mb-6 list-decimal ml-4">
              Click the dropdown next to your <span className="font-semibold">username</span>.
            </li>
            <li className="mb-2 list-decimal ml-4">
              Copy the <span className="font-semibold">Account ID</span> next to "My Account" and type it below.
            </li>
            <div className="flex items-center justify-center mb-2 border" style={{ width: 421, height: 358 }}>
              <img
                className="object-container"
                style={{ width: 421, height: 358 }}
                src={"/resources/howto/aws_accountIdSteps.jpg"}
                alt={"how do I get my aws account id?"}
              />
            </div>
            <CpFormItem>
              <CpLabel name={"aws.accountId"}>AWS Account ID</CpLabel>
              <CpInput name={"aws.accountId"} placeholder={"Type Account ID"} style={{ width: 220 }} />
              <CpErrorWarning name={"aws.accountId"} />
            </CpFormItem>
            {awsAccountId ? (
              <>
                <li className="list-decimal mt-6 mb-6 ml-4">
                  Go to <span className="font-semibold">Roles</span> in IAM and click{" "}
                  <span className="font-semibold">Create Role</span>. Select{" "}
                  <span className="font-semibold">AWS Account</span> option.
                </li>
                <div className="flex items-center justify-center mb-6 border" style={{ width: 800 }}>
                  <img
                    className="object-container"
                    style={{ width: 800 }}
                    src={"/resources/howto/aws-new-aws-account.png"}
                    alt={"select the option aws account"}
                  />
                </div>
                <li className="list-decimal mb-6 ml-4">
                  Click <span className="font-semibold">Next</span> twice, then name the role as{" "}
                  <span className="font-semibold">{instructions.data.bridgeRole}</span>
                  <CopyButtonWithApproval className="ml-1" value={instructions.data.bridgeRole} /> and click{" "}
                  <span className="font-semibold">Create role</span>.
                </li>
                <li className="list-decimal mb-6 ml-4">
                  Go to <span className="font-semibold">Policies</span> and click{" "}
                  <span className="font-semibold">Create Policy</span>.
                </li>
                <li className="list-decimal mb-2 ml-4">
                  Switch to the <span className="font-semibold">JSON</span> tab and paste the following connector policy:
                </li>
                <SyntaxHighlight
                  code={JSON.stringify(instructions.data.connectorPolicy, null, 2)}
                  language="json"
                  className="my-2"
                />
                <li className="list-decimal mb-6 ml-4">
                  Click <span className="font-semibold">Next</span>, name the policy as{" "}
                  <span className="font-semibold">cpln-connector</span>
                  <CopyButtonWithApproval className="ml-1" value={"cpln-connector"} /> and click{" "}
                  <span className="font-semibold">Create policy</span>.
                </li>
                <li className="list-decimal mb-2 ml-4">
                  Go to <span className="font-semibold">Roles</span>, select the{" "}
                  <span className="font-semibold">{instructions.data.bridgeRole}</span> role, click{" "}
                  <span className="font-semibold">Add permissions → Attach policies</span>, search for{" "}
                  {(instructions.data.accessPolicies as string[]).map((policy, index) => (
                    <span key={policy}>
                      <span className="font-semibold">{policy}</span>
                      {index !== (instructions.data.accessPolicies as string[]).length - 1 ? <span>, </span> : null}
                    </span>
                  ))}{" "}
                  and attach them.
                </li>
                <div className="flex items-center justify-center mb-6 border" style={{ width: 700 }}>
                  <img
                    className="object-container"
                    style={{ width: 700 }}
                    src={"/resources/howto/aws-new-attach-policies.png"}
                    alt={"click attach policies under permission tab"}
                  />
                </div>
                <li className="list-decimal mb-2 ml-4">
                  In the <span className="font-semibold">Trust relationships</span> tab, click{" "}
                  <span className="font-semibold">Edit trust policy</span> and paste the following:
                </li>
                <SyntaxHighlight
                  code={JSON.stringify(instructions.data.trustPolicy, null, 2)}
                  language="json"
                  className="my-2"
                />
                <li className="list-decimal mb-4 ml-4">
                  Click <span className="font-semibold">Update policy</span>.
                </li>
                <li className="list-decimal mb-4 ml-4">You can close this wizard.</li>
              </>
            ) : null}
          </ul>
        </>
      ) : null}
      <div className="my-4">
        <button type={"button"} className="ngfocus font-medium color-link" onClick={onCPLN}>
          Optionally you can use the Control Plane CLI (cpln)
          {continueWith === "cli" ? (
            <ChevronUp className="ml-1 inline-block" size={16} style={{ transform: "translateY(2px)" }} />
          ) : (
            <ChevronDown className="ml-1 inline-block" size={16} style={{ transform: "translateY(2px)" }} />
          )}
        </button>
      </div>
      {continueWith === "cli" ? (
        instructions.data.cli ? (
          <>
            <ul className="pl-8">
              <li className="list-decimal mb-2 ml-4">
                Install the <span className="font-semibold">Control Plane CLI</span>.
              </li>
              <SyntaxHighlight code="npm i -g @controlplane/cli" language="bash" className="my-2" />
              <li className="list-decimal mb-6 ml-4">
                Install the <ExternalLink url="https://aws.amazon.com/cli" label="AWS CLI" disableCopy /> and{" "}
                <ExternalLink
                  url="https://docs.aws.amazon.com/cli/latest/userguide/cli-chap-configure.html"
                  label="configure authentication"
                  disableCopy
                />
                .
              </li>
              <li className="list-decimal mb-2 ml-4">Type your AWS Account ID below.</li>
              <button
                type={"button"}
                className="ngfocus color-link font-medium mb-2"
                onClick={() => {
                  setShowAwsAccountIdGuide((x) => !x);
                }}
              >
                How do I find my AWS Account ID?
                {showAwsAccountIdGuide ? (
                  <ChevronUp className="ml-1 feather-icon inline-block" style={{ transform: "translateY(2px)" }} />
                ) : (
                  <ChevronDown className="ml-1 feather-icon inline-block" style={{ transform: "translateY(2px)" }} />
                )}
              </button>
              {showAwsAccountIdGuide ? (
                <>
                  <ul className="ml-8">
                    <li className="list-decimal mb-6 ml-4">
                      Sign into the{" "}
                      <ExternalLink url="https://console.aws.amazon.com" label="AWS Console" disableCopy />
                    </li>
                    <li className="mb-6 list-decimal ml-4">
                      Click the dropdown next to your <span className="font-semibold">username</span>.
                    </li>
                    <li className="mb-2 list-decimal ml-4">
                      Copy the <span className="font-semibold">Account ID</span> next to "My Account".
                    </li>
                  </ul>
                  <div
                    className="ml-8 flex items-center justify-center mb-4 border"
                    style={{ width: 421, height: 358 }}
                  >
                    <img
                      className="object-container"
                      style={{ width: 421, height: 358 }}
                      src={"/resources/howto/aws_accountIdSteps.jpg"}
                      alt={"how do I get my aws account id?"}
                    />
                  </div>
                </>
              ) : null}
              <CpFormItem>
                <CpLabel name={"aws.accountId"}>AWS Account ID</CpLabel>
                <CpInput name={"aws.accountId"} placeholder={"Type Account ID"} style={{ width: 220 }} />
                <CpErrorWarning name={"aws.accountId"} />
              </CpFormItem>
              {awsAccountId ? (
                <>
                  <li className="list-decimal mt-6 mb-2 ml-4">
                    Execute the following commands to create the cloud account, role, and policies.
                  </li>
                  <SyntaxHighlight
                    code={instructions.data.cli
                      .map((_cliStep: string) => {
                        let step = _cliStep;
                        step = step.replace("$ACCOUNT_NAME", name);
                        step = step.replace("$ACCOUNT_ID", awsAccountId);
                        return step;
                      })
                      .join(" && \\\n")}
                    language="bash"
                    className="my-2"
                  />
                  <li className="list-decimal my-2 ml-4">You're done. You can close this wizard.</li>
                </>
              ) : null}
            </ul>
          </>
        ) : null
      ) : null}
    </>
  );
};
